import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import Logo from './Logo';
import { FilterBarContext } from './filterBar';
import VodafoneBadge from '../components/VodafoneBadge';
import Avatar from '../components/Avatar';
import QuickSearch from './QuickSearch';
import NotifButton from './NotifButton';
import { IconActivity, IconBuilding, IconSearch, IconList, IconSettings, IconFilter, IconPhone, IconSignal } from '../components/Icons';

const FS_SUPPORTED = typeof document !== 'undefined' && !!document.documentElement.requestFullscreen;

const TITLES = { '/': 'Sistema', '/verifiche': 'AI 456', '/copertura': 'AI Copertura', '/vf': 'AI VF', '/analisi': 'Analisi', '/utilizzo': 'Utilizzo', '/regioni': 'Regioni', '/cerca': 'Verifica online', '/liste': 'Numeri', '/ssmate': 'AI SSMATE', '/copertura-ssmate': 'Copertura SSMATE', '/salute': 'Salute' };
const THEMES = [['green', 'Verde'], ['blue', 'Blu'], ['dark', 'Nero']];
const MODES = [['dark', 'Scuro'], ['light', 'Chiaro']];

const IconHome = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-7 9 7M5 10v10h14V10" /></svg>
);
const IconChart = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="11" width="3" height="6" /><rect x="12" y="7" width="3" height="10" /><rect x="17" y="13" width="3" height="4" /></svg>
);
const IconUsers = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
);
const IconMapPin = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
);
const IconBurger = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
);
const IconFs = ({ on, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {on ? <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /> : <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />}
  </svg>
);

export default function MobileLayout({ theme, mode, setTheme, setMode, onOpenInstall }) {
  const { user, logout, can } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);       // impostazioni (gear)
  const [navOpen, setNavOpen] = useState(false);  // menu hamburger
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterReg, setFilterReg] = useState(null); // tasto Filtri registrato dalla pagina corrente
  const [fs, setFs] = useState(typeof document !== 'undefined' && !!document.fullscreenElement);
  const title = TITLES[loc.pathname] || 'Sistema';

  const items = [
    { to: '/', label: 'Stato', icon: <IconHome size={18} />, show: true, sec: 'Generale' },
    { to: '/liste', label: 'Numeri', icon: <IconList size={18} />, show: can('numeri'), sec: 'Generale' },
    { to: '/analisi', label: 'Analisi', icon: <IconChart size={18} />, show: user?.ruolo === 'admin', sec: 'Generale' },
    { to: '/utilizzo', label: 'Utilizzo', icon: <IconUsers size={18} />, show: user?.ruolo === 'admin', sec: 'Generale' },
    { to: '/regioni', label: 'Regioni', icon: <IconMapPin size={18} />, show: can('analisi'), sec: 'Generale' },
    { to: '/verifiche', label: 'AI 456', icon: <IconPhone size={18} />, show: can('verifiche'), sec: 'Agenti' },
    { to: '/copertura', label: 'AI Copertura', icon: <IconSignal size={18} />, show: can('agente_copertura'), sec: 'Agenti' },
    { to: '/ssmate', label: 'AI SSMATE', icon: <IconBuilding size={18} />, show: can('ssmate'), sec: 'Agenti' },
    { to: '/copertura-ssmate', label: 'Copertura SSMATE', icon: <IconSignal size={18} />, show: can('ssmate_copertura'), sec: 'Agenti' },
    { to: '/vf', label: 'AI VF', icon: <VodafoneBadge size={18} />, show: can('gea'), sec: 'Agenti' },
    { to: '/cerca', label: 'Verifica online', icon: <IconSearch size={18} />, show: can('ricerca_online'), sec: 'Lavoro' },
    { to: '/salute', label: 'Salute', icon: <IconActivity size={18} />, show: can('salute'), sec: 'Lavoro' },
  ].filter((i) => i.show);
  const go = (to) => { setNavOpen(false); nav(to); };

  useEffect(() => {
    const h = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  return (
    <FilterBarContext.Provider value={setFilterReg}>
      <div className="m-top">
        <Logo size={26} />
        <h1>{title}</h1>
        {loc.pathname === '/' && <span className="livedot" />}
        {filterReg && (
          <button className="gear flt" onClick={filterReg.onOpen} aria-label="Filtri">
            <IconFilter size={18} />
            {filterReg.count > 0 && <span className="fbadge">{filterReg.count}</span>}
          </button>
        )}
        {can('ricerca_online') && (
          <button className="gear" onClick={() => { setSearchOpen(true); setMenu(false); setNavOpen(false); }} aria-label="Cerca numero">
            <IconSearch size={18} />
          </button>
        )}
        <button className="gear nav" onClick={() => { setNavOpen((o) => !o); setMenu(false); }} aria-label="Menu"><IconBurger size={18} /></button>
        <button className="gear" onClick={() => { setMenu((m) => !m); setNavOpen(false); }} aria-label="Impostazioni"><IconSettings size={18} /></button>

        {navOpen && (
          <>
            <div onClick={() => setNavOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
            <div className="m-navmenu">
              {items.map((it, idx) => (
                <React.Fragment key={it.to}>
                  {(idx === 0 || items[idx - 1].sec !== it.sec) && <div className="m-navsec">{it.sec}</div>}
                  <button className={loc.pathname === it.to ? 'on' : ''} onClick={() => go(it.to)}>
                    <span className="mi">{it.icon}</span>
                    <span className="ml">{it.label}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
          </>
        )}

        {menu && (
          <>
            <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
            <div className="m-menu">
              <div className="lbl">Tema</div>
              <div className="m-seg2">
                {THEMES.map(([id, lab]) => (
                  <button key={id} className={theme === id ? 'on' : ''} onClick={() => setTheme(id)}>{lab}</button>
                ))}
              </div>
              <div className="lbl">Modo</div>
              <div className="m-seg2">
                {MODES.map(([id, lab]) => (
                  <button key={id} className={mode === id ? 'on' : ''} onClick={() => setMode(id)}>{lab}</button>
                ))}
              </div>
              {FS_SUPPORTED && (
                <button className="m-fs" onClick={toggleFs}>
                  <IconFs on={fs} /> {fs ? 'Esci da schermo intero' : 'Schermo intero'}
                </button>
              )}
              <NotifButton />
              <button className="m-fs" onClick={() => { setMenu(false); onOpenInstall && onOpenInstall(); }}>Installa app</button>
              <button className="logout" onClick={logout}>Esci</button>
              <div className="m-who">
                <Avatar username={user?.username} size={34} color="var(--ink)" />
                <div style={{ minWidth: 0 }}>
                  <div className="m-who-name">{user?.username}</div>
                  <span className={`avatar-role-badge ${user?.ruolo === 'admin' ? 'admin' : 'op'}`}>
                    {user?.ruolo === 'admin' ? 'Amministratore' : 'Operatore'}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {searchOpen && (
        <div className="m-search-modal" onClick={() => setSearchOpen(false)}>
          <div className="box" onClick={(e) => e.stopPropagation()}>
            <div className="sh"><h2>Cerca un numero</h2>
              <button className="x" onClick={() => setSearchOpen(false)} aria-label="Chiudi">Chiudi</button>
            </div>
            <QuickSearch onStart={() => setSearchOpen(false)} />
          </div>
        </div>
      )}

      <div className={`m-content${loc.pathname === '/regioni' ? ' m-content-full' : ''}`}>
        <Outlet />
      </div>
    </FilterBarContext.Provider>
  );
}
